/* AR patch preview -- "Wanna see how it'll look?"
 *
 * Opens the rear camera and drops this design's patch cutout over the live
 * feed as a draggable / pinch-scalable / twist-to-rotate sticker; "Save"
 * composites it with the current frame and offers it via the share sheet.
 * Optionally runs on-device object detection (TensorFlow.js coco-ssd,
 * loaded lazily from a CDN) so "Scan" can auto-place the patch on whatever
 * you point at.
 *
 * NO sizing: the patch is freely resizable and products have their own
 * size variants, so implying a scale would just confuse. This is purely a
 * "how would it look" preview -- not tracked AR (the patch doesn't stick
 * when the phone moves). Deliberate: works in every phone browser, no paid
 * AR SDK, no continuous inference.
 */
(function () {
  "use strict";

  var CFG = window.__AR_PATCH__ || {};
  var PLACEHOLDER_SRC = CFG.placeholder || "";
  var PATCH_SRC = CFG.img || PLACEHOLDER_SRC;
  var PRODUCT_TITLE = CFG.title || "this patch";

  // coco-ssd classes worth auto-placing a patch onto. No sizes -- the patch
  // just lands centred on the biggest one it finds.
  var PLACEABLE = {
    laptop: 1, "cell phone": 1, tv: 1, book: 1, bottle: 1, cup: 1,
    "wine glass": 1, keyboard: 1, mouse: 1, remote: 1, backpack: 1,
    handbag: 1, suitcase: 1, "potted plant": 1, vase: 1, "teddy bear": 1,
    "sports ball": 1, chair: 1, couch: 1, bed: 1, "dining table": 1,
    refrigerator: 1, microwave: 1, oven: 1, sink: 1
  };

  var TFJS_URL =
    "https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.22.0/dist/tf.min.js";
  var COCO_URL =
    "https://cdn.jsdelivr.net/npm/@tensorflow-models/coco-ssd@2.2.3/dist/coco-ssd.min.js";

  var model = null;
  var modelPromise = null;
  var overlay = null;
  var stream = null;
  var patchImg = null;
  var usingPlaceholder = false;
  var els = {};

  // sticker state, in CSS pixels relative to the overlay
  var state = { cx: 0, cy: 0, w: 120, angle: 0 };
  var pointers = new Map();
  var gesture = null;

  function buzz(ms) {
    if (navigator.vibrate) {
      try { navigator.vibrate(ms); } catch (e) {}
    }
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = function () {
        reject(new Error("Failed to load " + src));
      };
      document.head.appendChild(s);
    });
  }

  function ensureModel() {
    if (model) return Promise.resolve(model);
    if (!modelPromise) {
      modelPromise = loadScript(TFJS_URL)
        .then(function () { return loadScript(COCO_URL); })
        .then(function () { return window.cocoSsd.load({ base: "lite_mobilenet_v2" }); })
        .then(function (m) { model = m; return m; })
        .catch(function (err) { modelPromise = null; throw err; });
    }
    return modelPromise;
  }

  function named(name, message) {
    var e = new Error(message);
    e.name = name;
    return e;
  }

  function tryLoad(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error("image load failed")); };
      img.src = src;
    });
  }

  // "Customise your own" products: build the patch from the customer's live
  // design on the page instead of loading a catalogue cutout.
  function buildCustomSrc() {
    var root = document.querySelector("[data-custom-personalization]");
    if (!root) return Promise.reject(named("ArtNotReady", ""));

    // Customise Your Own Text (Oct 16 2026): the page builds the exact print
    // design itself (font, gradient, outline, icon) -- use that.
    if (typeof root.customPersonalizationBuildCanvas === "function") {
      return root.customPersonalizationBuildCanvas().then(
        function (canvas) {
          return canvas.toDataURL("image/png");
        },
        function (err) {
          throw named("CustomNotReady", err.message);
        }
      );
    }

    if (root.getAttribute("data-mode") === "image") {
      var im = root.querySelector("[data-custom-image-preview] img");
      if (!im || !im.getAttribute("src")) {
        return Promise.reject(named("CustomNotReady", "Upload your image first, then tap this again."));
      }
      return Promise.resolve(im.src);
    }

    var mode = root.getAttribute("data-mode");

    // QR mode -- reuse the Yes panel's uploaded preview image verbatim, or
    // (No panel) build the exact same composite canvas the real cart image
    // uses, via the shared qr-canvas-builder.js, so AR preview and the
    // printed patch are guaranteed pixel-identical.
    if (mode === "qr") {
      var qrYesPanel = root.querySelector('[data-qr-panel="yes"]');
      if (qrYesPanel && !qrYesPanel.hidden) {
        var qrImg = root.querySelector("[data-custom-image-preview] img");
        if (!qrImg || !qrImg.getAttribute("src")) {
          return Promise.reject(named("CustomNotReady", "Upload your QR code image first, then tap this again."));
        }
        return Promise.resolve(qrImg.src);
      }

      var qrLinkInput = root.querySelector("[data-qr-link-input]");
      var qrColorSelect = root.querySelector("[data-qr-color-select]");
      var qrBackgroundSelect = root.querySelector("[data-qr-background-select]");
      var qrCaptionInput = root.querySelector("[data-qr-caption-input]");
      var qrCaptionPositionSelect = root.querySelector("[data-qr-caption-position-select]");
      var qrLink = qrLinkInput ? qrLinkInput.value.trim() : "";
      var qrColor = qrColorSelect ? qrColorSelect.value : "";
      var qrBackground = qrBackgroundSelect ? qrBackgroundSelect.value : "";

      if (!qrLink || !qrColor) {
        return Promise.reject(named("CustomNotReady", "Fill in your link and color first, then tap this again."));
      }
      if (typeof window.STPBuildQRCanvas !== "function") {
        return Promise.reject(named("ArtNotReady", ""));
      }
      return window
        .STPBuildQRCanvas({
          link: qrLink,
          color: qrColor,
          background: qrBackground,
          captionText: qrCaptionInput ? qrCaptionInput.value.trim() : "",
          captionPosition: qrCaptionPositionSelect ? qrCaptionPositionSelect.value : "Below"
        })
        .then(function (canvas) {
          return canvas.toDataURL("image/png");
        });
    }

    // font + colour are shared by text and social modes
    var fontSel = root.querySelector("[data-custom-font-select]");
    var opt = fontSel && fontSel.options[fontSel.selectedIndex];
    var fam = (opt && opt.getAttribute("data-font-family")) || "montserrat_bold";
    var colorSel = root.querySelector("[data-custom-color-select]");
    var color = colorSel ? colorSel.value : "White";
    var fontReady =
      document.fonts && document.fonts.load
        ? document.fonts.load("400 400px '" + fam + "'").catch(function () {})
        : Promise.resolve();

    // social mode -- platform logo + the typed handle, matching the real
    // printed patch. The preview label already carries the normalised handle
    // (Instagram values keep their leading "@").
    if (mode === "social") {
      var labelEl = root.querySelector("[data-custom-text-preview-label]");
      var handleInput = root.querySelector("[data-custom-text-input]");
      var handle = (labelEl && labelEl.textContent.trim()) || (handleInput ? handleInput.value.trim() : "");
      var platformSel = root.querySelector("[data-custom-platform-select]");
      var platform = platformSel ? platformSel.value : "";
      if (!platform) {
        return Promise.reject(named("CustomNotReady", "Choose YouTube or Instagram first, then tap this again."));
      }
      if (!handle) {
        return Promise.reject(named("CustomNotReady", "Type your handle first, then tap this again."));
      }
      var logoEl = root.querySelector("[data-custom-social-logo]");
      var logoSrc = logoEl
        ? (platform === "Instagram" ? logoEl.getAttribute("data-src-instagram") : logoEl.getAttribute("data-src-youtube"))
        : "";
      return fontReady.then(function () {
        return renderSocialPatch(handle, logoSrc, fam, color);
      });
    }

    // text mode -- render the typed text + chosen font/colour to a canvas
    var input = root.querySelector("[data-custom-text-input]");
    var text = input ? input.value.trim() : "";
    if (!text) {
      return Promise.reject(named("CustomNotReady", "Type your text first, then tap this again."));
    }
    return fontReady.then(function () {
      return renderTextPatch(text, fam, color);
    });
  }

  function renderTextPatch(text, fam, color) {
    var fill = color === "Black" ? "#111111" : "#ffffff";
    var stroke = fill === "#ffffff" ? "rgba(0,0,0,0.38)" : "rgba(255,255,255,0.55)";
    var px = 400;
    var pad = Math.round(px * 0.22);
    var c = document.createElement("canvas");
    var ctx = c.getContext("2d");
    ctx.font = px + "px '" + fam + "'";
    var m = ctx.measureText(text);
    var asc = m.actualBoundingBoxAscent || px * 0.82;
    var desc = m.actualBoundingBoxDescent || px * 0.24;
    c.width = Math.ceil(m.width) + pad * 2;
    c.height = Math.ceil(asc + desc) + pad * 2;

    ctx.font = px + "px '" + fam + "'";
    ctx.textBaseline = "alphabetic";
    ctx.lineJoin = "round";
    ctx.lineWidth = px * 0.07;
    ctx.strokeStyle = stroke;
    ctx.fillStyle = fill;
    ctx.strokeText(text, pad, pad + asc);
    ctx.fillText(text, pad, pad + asc);
    return c.toDataURL("image/png");
  }

  // social mode -- the platform logo (full colour) to the left of the
  // handle, sized to the handle's height, matching the printed patch and
  // the Operations OS render.
  function renderSocialPatch(text, logoSrc, fam, color) {
    var fill = color === "Black" ? "#111111" : "#ffffff";
    var stroke = fill === "#ffffff" ? "rgba(0,0,0,0.38)" : "rgba(255,255,255,0.55)";
    var px = 400;
    var pad = Math.round(px * 0.22);

    var meas = document.createElement("canvas").getContext("2d");
    meas.font = px + "px '" + fam + "'";
    var m = meas.measureText(text);
    var asc = m.actualBoundingBoxAscent || px * 0.82;
    var desc = m.actualBoundingBoxDescent || px * 0.24;
    var textW = Math.ceil(m.width);
    var blockH = Math.ceil(asc + desc);

    var logoH = blockH;
    var gap = Math.round(blockH * 0.28);

    return tryLoad(logoSrc)
      .catch(function () { return null; })
      .then(function (logo) {
        var logoW = logo ? Math.round(logoH * (logo.naturalWidth / logo.naturalHeight || 1)) : 0;
        var lead = logo ? logoW + gap : 0;

        var c = document.createElement("canvas");
        c.width = lead + textW + pad * 2;
        c.height = blockH + pad * 2;
        var ctx = c.getContext("2d");

        if (logo) ctx.drawImage(logo, pad, pad, logoW, logoH);

        ctx.font = px + "px '" + fam + "'";
        ctx.textBaseline = "alphabetic";
        ctx.lineJoin = "round";
        ctx.lineWidth = px * 0.07;
        ctx.strokeStyle = stroke;
        ctx.fillStyle = fill;
        ctx.strokeText(text, pad + lead, pad + asc);
        ctx.fillText(text, pad + lead, pad + asc);
        return c.toDataURL("image/png");
      });
  }

  function loadPatchImage() {
    if (patchImg) return Promise.resolve(patchImg);

    var isCustom = CFG.mode === "custom-text" || CFG.mode === "custom-image" || CFG.mode === "custom-social" || CFG.mode === "custom-qr";
    var srcPromise = isCustom ? buildCustomSrc() : Promise.resolve(PATCH_SRC);

    return srcPromise
      .then(function (src) {
        return tryLoad(src).catch(function () {
          // Only fall back to the stand-in badge for a failed *uploaded*
          // cutout. No artwork wired up, or a broken custom design -> bail
          // with a clear message rather than fake it.
          if (!isCustom && CFG.imgIsUpload && PLACEHOLDER_SRC && src !== PLACEHOLDER_SRC) {
            usingPlaceholder = true;
            return tryLoad(PLACEHOLDER_SRC);
          }
          throw named("ArtNotReady", "");
        });
      })
      .then(function (img) { patchImg = img; return img; });
  }

  // cover-fit mapping between the video's intrinsic pixels and screen pixels
  function coverMap() {
    var v = els.video;
    var cw = overlay.clientWidth;
    var ch = overlay.clientHeight;
    var vw = v.videoWidth || cw;
    var vh = v.videoHeight || ch;
    var scale = Math.max(cw / vw, ch / vh);
    return {
      scale: scale,
      offsetX: (cw - vw * scale) / 2,
      offsetY: (ch - vh * scale) / 2,
      vw: vw, vh: vh, cw: cw, ch: ch
    };
  }

  function renderPatch() {
    var map = coverMap();
    var minW = 36;
    var maxW = Math.min(map.cw, map.ch) * 0.98;
    state.w = Math.max(minW, Math.min(maxW, state.w));
    state.cx = Math.max(0, Math.min(map.cw, state.cx));
    state.cy = Math.max(0, Math.min(map.ch, state.cy));
    var p = els.patch;
    p.style.left = state.cx + "px";
    p.style.top = state.cy + "px";
    p.style.width = state.w + "px";
    p.style.transform =
      "translate(-50%, -50%) rotate(" + state.angle.toFixed(2) + "deg)";
  }

  function showHint(text, sticky) {
    els.hint.textContent = text;
    els.hint.hidden = false;
    if (els.hint._t) clearTimeout(els.hint._t);
    if (!sticky) {
      els.hint._t = setTimeout(function () { els.hint.hidden = true; }, 3800);
    }
  }

  function setStatus(html) {
    if (html === false) {
      els.status.hidden = true;
      els.status.innerHTML = "";
      return;
    }
    els.status.hidden = false;
    els.status.innerHTML = html;
  }

  // ---- detection ----
  function scan() {
    els.scanBtn.disabled = true;
    els.scanBtn.textContent = "Looking…";
    ensureModel()
      .then(function (m) { return m.detect(els.video, 10); })
      .then(function (preds) {
        els.scanBtn.disabled = false;
        els.scanBtn.textContent = "Scan again";

        var usable = (preds || []).filter(function (p) {
          return PLACEABLE[p.class] && p.score > 0.45;
        });
        if (!usable.length) {
          showHint(
            "Couldn't lock onto anything 🤷  Just drag and pinch the patch wherever you want it.",
            false
          );
          return;
        }
        usable.sort(function (a, b) {
          return b.bbox[2] * b.bbox[3] - a.bbox[2] * a.bbox[3];
        });
        var hit = usable[0];
        var map = coverMap();
        var boxW = hit.bbox[2] * map.scale;

        state.w = Math.max(60, boxW * 0.45);
        state.cx = (hit.bbox[0] + hit.bbox[2] / 2) * map.scale + map.offsetX;
        state.cy = (hit.bbox[1] + hit.bbox[3] / 2) * map.scale + map.offsetY;
        state.angle = 0;
        renderPatch();
        buzz(18);
        showHint("Popped it on your " + hit.class + " — drag, pinch, twist to nail the look.", false);
      })
      .catch(function (err) {
        els.scanBtn.disabled = false;
        els.scanBtn.textContent = "Scan again";
        showHint("Auto-placing's not working here. No stress — drag and pinch the patch into place.", false);
        console.warn("[ar-patch] scan failed", err);
      });
  }

  // ---- gestures ----
  function dist(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }
  function ang(a, b) { return Math.atan2(b.y - a.y, b.x - a.x); }

  function onPointerDown(e) {
    els.patch.setPointerCapture(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 1) {
      gesture = { mode: "drag", lastX: e.clientX, lastY: e.clientY };
    } else if (pointers.size === 2) {
      var pts = Array.from(pointers.values());
      gesture = {
        mode: "transform",
        startDist: dist(pts[0], pts[1]) || 1,
        startW: state.w,
        startAngle: ang(pts[0], pts[1]),
        baseAngle: state.angle
      };
    }
  }
  function onPointerMove(e) {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (!gesture) return;
    if (gesture.mode === "drag" && pointers.size === 1) {
      state.cx += e.clientX - gesture.lastX;
      state.cy += e.clientY - gesture.lastY;
      gesture.lastX = e.clientX;
      gesture.lastY = e.clientY;
      renderPatch();
    } else if (gesture.mode === "transform" && pointers.size >= 2) {
      var pts = Array.from(pointers.values());
      state.w = gesture.startW * (dist(pts[0], pts[1]) / gesture.startDist);
      state.angle =
        gesture.baseAngle + (ang(pts[0], pts[1]) - gesture.startAngle) * (180 / Math.PI);
      renderPatch();
    }
  }
  function onPointerUp(e) {
    pointers.delete(e.pointerId);
    if (pointers.size === 0) {
      gesture = null;
    } else if (pointers.size === 1) {
      var only = Array.from(pointers.values())[0];
      gesture = { mode: "drag", lastX: only.x, lastY: only.y };
    }
  }

  // ---- save / share ----
  function saveComposite() {
    var map = coverMap();
    var maxDim = 1600;
    var outScale = Math.min(1, maxDim / Math.max(map.vw, map.vh));
    var canvas = document.createElement("canvas");
    canvas.width = Math.round(map.vw * outScale);
    canvas.height = Math.round(map.vh * outScale);
    var ctx = canvas.getContext("2d");
    ctx.drawImage(els.video, 0, 0, canvas.width, canvas.height);

    var ratio = patchImg.naturalHeight / patchImg.naturalWidth || 1;
    var pw = state.w;
    var ph = state.w * ratio;
    var cx = ((state.cx - map.offsetX) / map.scale) * outScale;
    var cy = ((state.cy - map.offsetY) / map.scale) * outScale;
    var vw = (pw / map.scale) * outScale;
    var vh = (ph / map.scale) * outScale;

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((state.angle * Math.PI) / 180);
    ctx.drawImage(patchImg, -vw / 2, -vh / 2, vw, vh);
    ctx.restore();

    canvas.toBlob(
      function (blob) {
        if (!blob) return;
        var file = new File([blob], "patch-on-your-thing.png", { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          navigator.share({ files: [file], title: PRODUCT_TITLE }).catch(function () {});
        } else {
          var url = URL.createObjectURL(blob);
          var a = document.createElement("a");
          a.href = url;
          a.download = file.name;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
        }
        showHint("Saved it 🎉", false);
      },
      "image/png",
      0.92
    );
  }

  // ---- lifecycle ----
  function buildOverlay() {
    overlay = document.createElement("div");
    overlay.className = "arp-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-label", "Preview this patch with your camera");
    overlay.innerHTML =
      '<video class="arp-video" playsinline muted autoplay></video>' +
      '<img class="arp-patch" alt="" draggable="false" />' +
      '<div class="arp-top">' +
      '<div class="arp-title">Wanna see how it&rsquo;ll look?<span>Not real AR, just vibes. Drag, pinch, twist.</span></div>' +
      '<button type="button" class="arp-close" aria-label="Close">&times;</button>' +
      "</div>" +
      '<p class="arp-hint" hidden></p>' +
      '<div class="arp-bar">' +
      '<button type="button" class="arp-btn arp-scan">Scan a thing</button>' +
      '<button type="button" class="arp-btn arp-btn--primary arp-save">Keep the shot</button>' +
      "</div>" +
      '<div class="arp-status" hidden></div>';
    document.body.appendChild(overlay);

    els.video = overlay.querySelector(".arp-video");
    els.patch = overlay.querySelector(".arp-patch");
    els.hint = overlay.querySelector(".arp-hint");
    els.status = overlay.querySelector(".arp-status");
    els.scanBtn = overlay.querySelector(".arp-scan");
    els.saveBtn = overlay.querySelector(".arp-save");

    overlay.querySelector(".arp-close").addEventListener("click", close);
    els.scanBtn.addEventListener("click", scan);
    els.saveBtn.addEventListener("click", saveComposite);
    els.patch.addEventListener("pointerdown", onPointerDown);
    els.patch.addEventListener("pointermove", onPointerMove);
    els.patch.addEventListener("pointerup", onPointerUp);
    els.patch.addEventListener("pointercancel", onPointerUp);
    window.addEventListener("resize", renderPatch);
  }

  function startCamera() {
    return navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      })
      .then(function (s) {
        stream = s;
        els.video.srcObject = s;
        return els.video.play();
      });
  }

  function open() {
    if (overlay) return;
    document.body.classList.add("arp-lock");
    buildOverlay();
    setStatus('<div class="arp-spinner"></div><div>Waking up the camera…</div>');

    // Build the patch first (fast; a bad custom design fails here before we
    // ever touch the camera), then start the camera.
    loadPatchImage()
      .then(function () { return startCamera(); })
      .then(function () {
        els.patch.src = patchImg.src;
        var map = coverMap();
        state.cx = map.cw / 2;
        state.cy = map.ch / 2;
        state.w = Math.min(map.cw, map.ch) * 0.34;
        state.angle = 0;
        renderPatch();
        setStatus(false);
        showHint(
          usingPlaceholder
            ? "Real art's still loading — showing a stand-in for now."
            : "Drag it around, pinch to resize, twist to rotate. Or hit “Scan a thing” to drop it on something.",
          false
        );
        ensureModel().catch(function () {});
      })
      .catch(function (err) {
        console.warn("[ar-patch] open failed", err);
        var msg =
          err && err.name === "CustomNotReady"
            ? err.message
            : err && err.name === "ArtNotReady"
            ? "This design's preview isn't ready yet — check back soon!"
            : err && (err.name === "NotAllowedError" || err.name === "SecurityError")
            ? "Camera's blocked. Switch it on for this site in your browser settings and give it another go."
            : err && err.name === "NotFoundError"
            ? "No camera on this device 🤔"
            : "The camera didn't want to start here. Try again in a sec?";
        setStatus(
          "<div>" + msg + '</div><button type="button" class="arp-btn arp-btn--primary arp-status-close">Close</button>'
        );
        var b = els.status.querySelector(".arp-status-close");
        if (b) b.addEventListener("click", close);
      });
  }

  function close() {
    if (stream) {
      stream.getTracks().forEach(function (t) { t.stop(); });
      stream = null;
    }
    window.removeEventListener("resize", renderPatch);
    if (overlay) { overlay.remove(); overlay = null; }
    els = {};
    pointers.clear();
    gesture = null;
    document.body.classList.remove("arp-lock");
  }

  function unsupported() {
    return (
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia ||
      typeof Promise === "undefined"
    );
  }

  document.addEventListener("click", function (e) {
    var trigger = e.target.closest && e.target.closest("[data-ar-open]");
    if (!trigger) return;
    e.preventDefault();
    if (unsupported()) {
      alert("You'll need something with a camera and a semi-modern browser for this one.");
      return;
    }
    open();
  });
})();
